import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import MplusApi from "Services/Mplus";
import {
  FINANCING_STATUS,
  apiErrorMessage,
  financingProduct,
  formatAmount,
  formatDate,
  normalizePeriods,
  toAssetUrl,
  unwrap,
  unwrapList,
  useHasRole,
} from "../mplusUtils";
import PaymentTracking from "./index";

// Statut d'un prélèvement LewooPay (section 9) → statut affiché.
const COLLECTION_STATUS = { SUCCESS: "paye", PARTIAL: "partiel", FAILED: "retard", SKIPPED: "pause" };

// Clés du panneau d'éligibilité ↔ règles de l'API (section 5).
const RULES = [
  { key: "chauffeur", rule: "kyc_driver", label: () => "Compte chauffeur actif (LewooPay)", help: true },
  { key: "anciennete", rule: "seniority", label: (r) => `Ancienneté minimale (≥ ${r?.min_months ?? 6} mois)` },
  { key: "note", rule: "rating", label: (r) => `Note minimale (≥ ${String(r?.min ?? 4).replace(".", ",")})` },
  { key: "impaye", rule: "no_arrears", label: () => "Aucun impayé" },
  { key: "ratio", rule: "debt_ratio", label: (r) => `Ratio mensualités / revenu (≤ ${r?.max_pct ?? 40} %)` },
];

const today = () => new Date().toISOString().slice(0, 10);

const PaymentTrackingContainer = ({ application, financing, notify, onBack }) => {
  const hasRole = useHasRole();
  const [periods, setPeriods] = useState([]);
  const [collections, setCollections] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractsError, setContractsError] = useState(false);
  const [downloadingContractId, setDownloadingContractId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseUntil, setPauseUntil] = useState("");
  const [pauseReason, setPauseReason] = useState("");
  const [pausedUntil, setPausedUntil] = useState(financing?.collection_paused_until || null);

  const load = useCallback(async () => {
    if (!financing?.id) return;
    setLoading(true);
    const [schedule, history, rules, contractResponse] = await Promise.allSettled([
      MplusApi.getFinancingSchedule(financing.id),
      MplusApi.getFinancingCollections(financing.id),
      MplusApi.getEligibilitySettings(),
      MplusApi.getFinancingContracts(financing.id),
    ]);
    if (schedule.status === "fulfilled") setPeriods(normalizePeriods(unwrap(schedule.value)?.periods || [], financing.principal_amount));
    else notify(apiErrorMessage(schedule.reason, "Impossible de charger l'échéancier."), "error");
    if (history.status === "fulfilled") setCollections(unwrapList(history.value));
    if (rules.status === "fulfilled") setSettings(unwrap(rules.value) || null);
    setContractsError(contractResponse.status === "rejected");
    if (contractResponse.status === "fulfilled") {
      const data = unwrap(contractResponse.value) || {};
      setContracts([
        { contract: data.sale_contract, label: "Contrat de vente" },
        { contract: data.guarantee_contract, label: "Contrat de caution" },
      ].filter((item) => item.contract?.id).map(({ contract, label }) => ({
        id: contract.id,
        label,
        reference: contract.reference_number || "",
        version: contract.current_version,
      })));
    } else setContracts([]);
    setLoading(false);
  }, [financing, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const view = useMemo(() => {
    const now = today();
    const overdue = periods.filter((period) => !period.paid && period.dueDate && period.dueDate.slice(0, 10) < now);
    const next = periods.find((period) => !period.paid && (!period.dueDate || period.dueDate.slice(0, 10) >= now));
    const collected = collections.reduce((sum, item) => sum + (Number(item.amount_collected) || 0), 0);
    const arrears = financing?.arrears_amount ?? overdue.reduce((sum, period) => sum + period.total, 0);
    return {
      summary: {
        attendu: { montant: formatAmount(next?.total), detail: next ? `Prochaine échéance • ${formatDate(next.dueDate)}` : "Aucune échéance à venir" },
        encaisse: { montant: formatAmount(collected), detail: `${periods.filter((period) => period.paid).length} échéances sur ${periods.length}` },
        arrieres: { montant: formatAmount(arrears), detail: `${overdue.length} échéance(s) en retard` },
      },
      payments: collections.map((item, index) => ({
        id: item.id || index,
        date: formatDate(item.collection_date),
        montant: formatAmount(item.amount_collected || item.amount_due),
        statut: COLLECTION_STATUS[item.status] || "pause",
        mode: item.status === "SUCCESS" || item.status === "PARTIAL" ? "LewooPay" : item.reason || "—",
        reference: item.reference || "--",
      })),
      schedule: periods.map((period) => ({
        id: period.period,
        date: formatDate(period.dueDate),
        montant: formatAmount(period.total),
        paye: formatAmount(period.paidAmount),
        reste: formatAmount(period.outstanding),
        statut: period.paid ? "paye" : overdue.includes(period) ? "retard" : "a_venir",
      })),
      totals: {
        total: formatAmount(periods.reduce((sum, period) => sum + period.total, 0)),
        paid: formatAmount(periods.reduce((sum, period) => sum + (period.paidAmount ?? 0), 0)),
        outstanding: formatAmount(periods.reduce((sum, period) => sum + (period.outstanding ?? (period.paid ? 0 : period.total)), 0)),
      },
    };
  }, [collections, financing, periods]);

  const eligibility = useMemo(
    () => RULES.map((item) => ({ key: item.key, label: item.label(settings?.[item.rule]), help: item.help, enabled: Boolean(settings?.[item.rule]?.enabled) })),
    [settings]
  );

  const handleDownloadContract = async (contract) => {
    if (downloadingContractId) return;
    setDownloadingContractId(contract.id);
    try {
      const response = await MplusApi.downloadContract(contract.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `contrat-${contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de télécharger le contrat."), "error");
    } finally {
      setDownloadingContractId(null);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = RULES.reduce((result, item) => ({ ...result, [item.rule]: { enabled: Boolean(values[item.key]) } }), {});
      setSettings(unwrap(await MplusApi.updateEligibilitySettings(payload)) || settings);
      notify("Règles d'éligibilité enregistrées.", "success");
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible d'enregistrer les règles."), "error");
    } finally {
      setSaving(false);
    }
  };

  // Suspendre jusqu'à une date, ou reprendre (until: null).
  const submitPause = async (until) => {
    setSaving(true);
    try {
      const data = unwrap(await MplusApi.runFinancingAction(financing.id, "pause", { until, reason: pauseReason.trim() || undefined }));
      setPausedUntil(data?.collection_paused_until ?? until);
      notify(until ? `Prélèvements suspendus jusqu'au ${formatDate(until)}.` : "Prélèvements repris.", "success");
      setPauseOpen(false);
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de modifier la suspension."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress size={28} /></Box>;
  }

  const product = financingProduct(financing, application);
  const paused = pausedUntil && pausedUntil.slice(0, 10) >= today();

  return (
    <>
      <PaymentTracking
        key={settings ? "loaded" : "empty"}
        dossier={{
          reference: financing?.reference || application?.reference,
          statut: paused ? `Prélèvements suspendus jusqu'au ${formatDate(pausedUntil)}` : (FINANCING_STATUS[financing?.status]?.label || "Dossier actif"),
          vehicule: { nom: product?.name || application?.vehicleMake, immatriculation: financing?.asset?.plate || financing?.delivery?.plate || "", image: toAssetUrl(product?.image_url) },
        }}
        contracts={contracts}
        contractsError={contractsError}
        downloadingContractId={downloadingContractId}
        onDownloadContract={handleDownloadContract}
        onRetryContracts={load}
        summary={view.summary}
        payments={view.payments}
        schedule={view.schedule}
        scheduleTotals={view.totals}
        canSuspend={hasRole("FINANCE")}
        canSaveSettings={hasRole()}
        onCopy={(reference) => {
          navigator.clipboard?.writeText(reference).then(
            () => notify("Référence copiée.", "success"),
            () => notify("Copie impossible.", "error")
          );
        }}
        eligibility={eligibility}
        saving={saving}
        suspendLabel={paused ? "Reprendre les prélèvements" : "Suspendre temporairement"}
        onBack={onBack}
        onSuspend={() => (paused ? submitPause(null) : setPauseOpen(true))}
        onSave={handleSave}
      />
      <Dialog open={pauseOpen} onClose={() => !saving && setPauseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Suspendre les prélèvements</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#526276", mb: 2 }}>Aucun prélèvement LewooPay ne sera tenté jusqu'à la date choisie.</Typography>
          <TextField fullWidth required type="date" size="small" label="Jusqu'au" InputLabelProps={{ shrink: true }} inputProps={{ min: today() }} value={pauseUntil} onChange={(event) => setPauseUntil(event.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth multiline minRows={2} size="small" label="Motif" value={pauseReason} onChange={(event) => setPauseReason(event.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPauseOpen(false)} sx={{ textTransform: "none" }}>Annuler</Button>
          <Button variant="contained" disabled={!pauseUntil || saving} onClick={() => submitPause(pauseUntil)} sx={{ textTransform: "none", bgcolor: "#0B6B4B", "&:hover": { bgcolor: "#095A3F" } }}>
            Suspendre
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentTrackingContainer;
